import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tooltip } from 'reactstrap';
import moment from 'moment';
import axios from 'axios';
import { useCompromisedEmployers, SortOrder, CompromisedEmployerItem } from '../hooks/useCompromisedEmployers';
import { useCompromisedEmployerAuditTrail } from '../hooks/useCompromisedEmployerAuditTrail';
import { useSearchDebounce } from '../hooks/useSearchDebounce';
import useDebouncedLoading from '../hooks/useDebouncedLoading';
import { Pager } from '../../../../components/pager';
import { DataTable, TableColumn } from '../../../../components/DataTable';
import { SearchInput } from '../../../../components/SearchInput';
import { EmptyState } from '../../../../components/EmptyState';
import { Pill, PillVariant } from '../../../../components/Pill';
import AuditTrail from './AuditTrail';
import AddCompromisedEmployerForm from './AddCompromisedEmployerForm';
import EditCompromisedEmployerForm from './EditCompromisedEmployerForm';
import { environment } from '../../../../environment/environment';
import toast from '../../../../utils/toast';
import styles from './RiskyIdNumbersTab.module.css';

const CompromisedEmployersTab: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [allCompromisedIds, setAllCompromisedIds] = useState<number[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<CompromisedEmployerItem | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
  const [expandedRowEmployerId, setExpandedRowEmployerId] = useState<number | null>(null);
  const [page, setPage] = useState<number>(1);
  const [activeOnly, setActiveOnly] = useState<boolean>(true);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Desc);
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
  const [isOpeningDrawer, setIsOpeningDrawer] = useState<boolean>(false);
  const { searchQuery, debouncedValue, setSearchQuery } = useSearchDebounce('', 500);

  const { data, loading, totalPages, refetch } = useCompromisedEmployers(debouncedValue, page, activeOnly, 'is_compromised_date_updated', sortOrder);
  const debouncedLoading = useDebouncedLoading(loading, 300);
  const { data: auditData, loading: auditLoading } = useCompromisedEmployerAuditTrail(expandedRowEmployerId);

  useEffect(() => {
    setTooltipOpen({});
  }, [data]);

  const openDrawer = useCallback(async () => {
    setIsOpeningDrawer(true);
    try {
      const response = await axios.get<{ results: Array<{ id: number }> }>(
        `${environment.baseUrl}/employer/compromised-list/`,
        { params: { page_size: 10000, active_only: false } }
      );
      setAllCompromisedIds(response.data.results.map((item) => item.id));
    } catch {
      toast.error('Could not load compromised employers list. Please try again.');
      setAllCompromisedIds([]);
      return;
    } finally {
      setIsOpeningDrawer(false);
    }
    setIsDrawerOpen(true);
  }, [data]);

  const toggleTooltip = (tooltipId: string) => {
    setTooltipOpen(prev => ({
      ...prev,
      [tooltipId]: !prev[tooltipId]
    }));
  };

  const handleEdit = (row: CompromisedEmployerItem) => {
    setEditingItem(row);
    setIsEditModalOpen(true);
    setExpandedRows(new Set());
    setExpandedRowEmployerId(null);
  };

  const handleRowToggle = (rowId: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
      setExpandedRowEmployerId(null);
    } else {
      newExpandedRows.clear();
      newExpandedRows.add(rowId);
      const row = data.find(item => item.id === rowId);
      setExpandedRowEmployerId(row?.id ?? null);
    }
    setExpandedRows(newExpandedRows);
  };

  const auditFieldLabels = {
    is_compromised: 'Compromised',
    is_compromised_reason: 'Reason'
  };

  const renderExpandedRow = () => <AuditTrail data={auditData} loading={auditLoading} fieldLabels={auditFieldLabels} />;

  const getRowClassName = (row: CompromisedEmployerItem) => row.is_compromised === false ? styles.inactiveRow : '';

  const columns: TableColumn[] = [
    {
      key: 'name',
      label: 'Employer',
      width: '300px',
      render: (value: string) => (
        <div className={styles.employerNameCell} title={value}>{value}</div>
      )
    },
    {
      key: 'is_compromised_reason',
      label: 'Reason',
      width: '400px',
      className: styles.reasonColumn,
      render: (value: any, row: any) => {
        if (!value) return '';
        const truncated = value.length > 100 ? `${value.substring(0, 100)}...` : value;
        const tooltipId = `reason-tooltip-${row.id}`;

        return (
          <div className={styles.reasonCell}>
            <span
              id={tooltipId}
              className={value.length > 100 ? styles.clickableText : ''}
            >
              {truncated}
            </span>
            {value.length > 100 && (
              <Tooltip
                target={tooltipId}
                isOpen={tooltipOpen[tooltipId] || false}
                toggle={() => toggleTooltip(tooltipId)}
                placement="top"
              >
                {value}
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      key: 'is_compromised_date_updated',
      label: (
        <button
          type="button"
          className={styles.sortableHeader}
          onClick={() => {
            setSortOrder(prev => prev === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc);
            setPage(1);
          }}
          aria-label={`Sort by date ${sortOrder === SortOrder.Desc ? 'ascending' : 'descending'}`}
        >
          Date Updated <span className={styles.sortArrow}>{sortOrder === SortOrder.Desc ? '↓' : '↑'}</span>
        </button>
      ),
      render: (value: string) => value ? moment(value).format('DD MMM YY HH:mm') : '-'
    },
    ...(!activeOnly ? [{
      key: 'is_compromised',
      label: 'Status',
      width: '120px',
      render: (_value: unknown, row: CompromisedEmployerItem) => {
        const isActive = row.is_compromised !== false;
        return (
          <Pill variant={isActive ? PillVariant.Success : PillVariant.Danger}>
            {isActive ? 'Active' : 'Inactive'}
          </Pill>
        );
      }
    }] as TableColumn[] : []),
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (value: any, row: any) => {
        const editTooltipId = `edit-tooltip-${row.id}`;

        return (
          <div className={styles.actionButtonsContainer}>
            <button
              type="button"
              id={editTooltipId}
              className={`${styles.actionButton} ${styles.editButton}`}
              onClick={() => handleEdit(row)}
              aria-label="Edit compromised employer"
            >
              <i className="fas fa-pen" />
            </button>
            <Tooltip
              target={editTooltipId}
              isOpen={tooltipOpen[editTooltipId] || false}
              toggle={() => toggleTooltip(editTooltipId)}
              placement="top"
            >
              Edit
            </Tooltip>
          </div>
        );
      }
    }
  ];

  const emptyState = (
    <EmptyState
      icon="fas fa-building"
      message="No employers have been flagged as compromised"
      iconColor="#ffc3cc"
      iconSize="0.75rem"
    />
  );

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.leftControls}>
          <SearchInput
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              setPage(1);
              setExpandedRows(new Set());
              setExpandedRowEmployerId(null);
            }}
            placeholder="Search Employers"
          />
          <div className={styles.filterToggle}>
            <div className={styles.switchContainer}>
              <input
                className={styles.switchInput}
                type="checkbox"
                id="active-only-toggle-compromised"
                checked={activeOnly}
                onChange={() => {
                  setActiveOnly(prev => !prev);
                  setPage(1);
                  setExpandedRows(new Set());
                  setExpandedRowEmployerId(null);
                }}
                role="switch"
                aria-checked={activeOnly}
                aria-label="Show active compromised employers only"
              />
              <label
                htmlFor="active-only-toggle-compromised"
                className={`${styles.toggleSwitch} ${activeOnly ? styles.active : ''}`}
                aria-hidden="true">{/* skipcq: JS-0752 */}
                <div className={`${styles.toggleSlider} ${activeOnly ? styles.active : ''}`} />
              </label>
            </div>
            <span className={styles.switchLabel}>
              Show active only
            </span>
          </div>
        </div>
        <button
          type="button"
          className={`btn btn-primary ${styles.addButton}`}
          onClick={openDrawer}
          disabled={isOpeningDrawer}
        >
          <i className="fas fa-plus mr-1" />
          {isOpeningDrawer ? 'Loading...' : 'Flag Employer'}
        </button>
      </div>

      <Card className="shadow border-0">
        <div className={`table-responsive ${styles.tableContainer}`}>
          <DataTable
            columns={columns}
            data={data}
            loading={debouncedLoading}
            emptyState={emptyState}
            skeletonRows={5}
            expandableRows
            expandedRowRender={renderExpandedRow}
            expandedRows={expandedRows}
            onRowToggle={handleRowToggle}
            getRowClassName={getRowClassName}
          />
        </div>
      </Card>

      {totalPages > 1 && (
        <div className={styles.pagerWrapper}>
          <div className={styles.pagerContainer}>
            <Pager
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <AddCompromisedEmployerForm
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={refetch}
        compromisedEmployerIds={allCompromisedIds}
      />

      <EditCompromisedEmployerForm
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => { refetch(); setEditingItem(null); }}
        employer={editingItem}
      />

    </>
  );
};

export default CompromisedEmployersTab;
